// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    Editor,
} from '@tinymce/tinymce-react'
import {
    useDispatch,
} from 'react-redux'

// components
import {
} from 'components'

// data
import {
    media_url_site,
    media_version_site,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ErrorHelper825,
    FormLabel825,
} from 'pages'

// serializer
import {
    MainFontSerializer,
    TranslationHelper,
} from 'serializers/site'

// props
type RichTextInputV2825Props = {
    directionRtl?: boolean
    disabled?: boolean
    error?: string
    fonts: MainFontSerializer[] | undefined
    helperText?: string
    label?: string
    marginBottom?: number
    name: string
    objectId: number | undefined
    onChange: React.Dispatch<any>
    onRefresh?: () => void
    presetColors?: string[]
    tinymceStylesObject?: any
    tinymceStylesString: string
    translation?: TranslationHelper
    value: string
}

// main
export const RichTextInputV2825: React.FC<RichTextInputV2825Props> = React.memo(({
    directionRtl,
    disabled,
    error,
    fonts,
    helperText,
    label,
    marginBottom,
    name,
    objectId,
    onChange,
    onRefresh,
    presetColors,
    tinymceStylesObject,
    tinymceStylesString,
    translation,
    value,
}) => {

    const dispatch = useDispatch()

    const [initialValue, setInitialValue] = useState<string>(value)
    const [isLoading, setIsLoading] = useState(false)

    let wto: any

    useEffect(() => {
        setInitialValue(value)
        setIsLoading(true)
        clearTimeout(wto)
        wto = setTimeout(() => {
            setIsLoading(false)
        }, 100)
    }, [objectId])

    function onInputChange(value: string | null | undefined) {
        try {
            onChange({
                name: name,
                value: value,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'RichTextInputV2825',
                'onInputChange',
            ))
        }
    }

    let fontFamilies = ''
    const contentCss: string[] = []
    const fontFamilyArray: string[] = []
    if (fonts?.length! > 0) {
        fonts!.map((val) => {
            if (!fontFamilyArray.includes(val.name)) {
                fontFamilyArray.push(val.name)
                fontFamilies += `${val.name};`
                if (val.category === 'manual') {
                    contentCss.push(`${media_url_site}global/fonts/${val.name}/stylesheet.css${media_version_site}`)
                } else {
                    contentCss.push(`//fonts.googleapis.com/css2?family=${val.name}:wght@100;200;300;400;500;600;700;800;900`)
                }
            }
            return false
        })
    }

    const richTextStyles = `
        blockquote {
            border-left: 5px solid #ccc;
            font-style: italic;
            padding: 10px 0 10px 20px;
        }
        ${tinymceStylesObject?.defaultMargin !== 'false' ? `
        h1, h2, h3, h4, h5, h6, p {
            margin: 0;
        }
        ` : ''}
        h1, h2, h3, h4, h5, h6 {
            font-weight: normal;
        }
    `
    const newTinymceStylesString = `.mce-content-body {${tinymceStylesString}} ${richTextStyles}`

    if (label || translation) {
        return (
            <div
                className={`input-wrap-825${disabled ? ' disabled' : ''}`}
                style={{
                    marginBottom: marginBottom ? `${marginBottom}px` : undefined,
                }}
            >
                <div className='input-label-wrap'>
                    <FormLabel825
                        fonts={fonts}
                        formInput='richText'
                        helperText={helperText}
                        label={label || ''}
                        name={name}
                        onRefresh={onRefresh}
                        translation={translation}
                        translationDefaultValue={value}
                    />
                </div>
                <div className='rich-text-input-825'>
                    {!isLoading && (
                        <Editor
                            disabled={disabled}
                            init={{
                                color_map: presetColors ? ['#000000', '#FFFFFF', ...presetColors] : undefined,
                                content_css: contentCss,
                                content_style: newTinymceStylesString,
                                height: 200,
                                license_key: 'gpl',
                                link_assume_external_targets: 'http',
                                link_default_target: '_blank',
                                menubar: false,
                                plugins: [
                                    'bold',
                                    'fullscreen',
                                    'italic',
                                    'link',
                                    'paste',
                                ],

                                toolbar: 'fullscreen | clearFontFormatting | bold italic | undo redo',

                                setup: function (editor: any) {
                                    // Add custom command to clear formatting
                                    editor.addCommand('clearFontFormatting', function () {
                                        editor.execCommand('mceToggleFormat', false, 'forecolor')
                                        editor.execCommand('mceToggleFormat', false, 'backcolor')
                                    })

                                    // Add custom button using modern TinyMCE API
                                    editor.ui.registry.addButton('clearFontFormatting', {
                                        text: 'Reset Text',
                                        tooltip: 'Reset text to default style (remove colors)',
                                        onAction: function () {
                                            editor.execCommand('clearFontFormatting')
                                        }
                                    })
                                },

                                // Only allow bold & italic styles on paste
                                paste_word_valid_styles: {
                                    '*': ['font-weight', 'font-style'],
                                },
                                paste_webkit_styles: 'font-weight,font-style',
                                valid_styles: {
                                    '*': 'font-weight,font-style',
                                },

                                // Block <img> and any base64 image pasting
                                paste_data_images: false,
                                paste_preprocess: (plugin, args) => {
                                    args.content = args.content.replace(/<img[^>]*>/gi, '')
                                },

                                // // Allow only these tags
                                // valid_elements: 'p,strong/b,em/i',
                                // extended_valid_elements: '', // clears any previously allowed extended elements

                                // // Ensure <p>&nbsp;</p> is not stripped
                                // cleanup: false,
                                // verify_html: false,
                                // protect: [/<p>(&nbsp;|\s)*<\/p>/g],

                                // // Optional: Avoid converting &nbsp; to normal space
                                // entities: '160,nbsp',
                            }}
                            initialValue={initialValue}
                            onEditorChange={(e) => onInputChange(e)}
                        />
                    )}
                </div>
                <ErrorHelper825 error={error} />
            </div>
        )
    }

    return (
        <div className='rich-text-input-825'>
            {!isLoading && (
                <Editor
                    disabled={disabled}
                    init={{
                        color_map: presetColors ? ['#000000', '#FFFFFF', ...presetColors] : undefined,
                        content_css: contentCss,
                        content_style: newTinymceStylesString,
                        height: 200,
                        license_key: 'gpl',
                        link_assume_external_targets: 'http',
                        link_default_target: '_blank',
                        menubar: false,
                        plugins: [
                            'bold',
                            'fullscreen',
                            'italic',
                            'link',
                            'paste',
                        ],

                        toolbar: 'fullscreen | clearFontFormatting | bold italic | undo redo',

                        setup: function (editor: any) {
                            // Add custom command to clear formatting
                            editor.addCommand('clearFontFormatting', function () {
                                editor.execCommand('mceToggleFormat', false, 'forecolor')
                                editor.execCommand('mceToggleFormat', false, 'backcolor')
                            })

                            // Add custom button using modern TinyMCE API
                            editor.ui.registry.addButton('clearFontFormatting', {
                                text: 'Reset Text',
                                tooltip: 'Reset text to default style (remove colors)',
                                onAction: function () {
                                    editor.execCommand('clearFontFormatting')
                                }
                            })
                        },

                        // Only allow bold & italic styles on paste
                        paste_word_valid_styles: {
                            '*': ['font-weight', 'font-style'],
                        },
                        paste_webkit_styles: 'font-weight,font-style',
                        valid_styles: {
                            '*': 'font-weight,font-style',
                        },

                        // Block <img> and any base64 image pasting
                        paste_data_images: false,
                        paste_preprocess: (plugin, args) => {
                            args.content = args.content.replace(/<img[^>]*>/gi, '')
                        },

                        // // Allow only these tags
                        // valid_elements: 'p,strong/b,em/i',
                        // extended_valid_elements: '', // clears any previously allowed extended elements

                        // // Ensure <p>&nbsp;</p> is not stripped
                        // cleanup: false,
                        // verify_html: false,
                        // protect: [/<p>(&nbsp;|\s)*<\/p>/g],

                        // // Optional: Avoid converting &nbsp; to normal space
                        // entities: '160,nbsp',
                    }}
                    initialValue={initialValue}
                    onEditorChange={(e) => onInputChange(e)}
                />
            )}
            {marginBottom && (
                <div
                    style={{
                        marginBottom: `${marginBottom}px`,
                    }}
                />
            )}
        </div>
    )
})
